import { motion } from "framer-motion";
import { MapPin, Phone, Calendar, ArrowUpRight } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { SALON_INFO } from "../data/assets";

export const Contact = () => {
  const { t } = useLang();

  return (
    <section
      id="contact"
      className="relative py-24 md:py-36 bg-[#050505] overflow-hidden"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-16 md:mb-20 max-w-3xl"
        >
          <div className="flex items-center gap-4 mb-6">
            <span className="h-px w-10 bg-[#C5A059]" />
            <span className="text-xs uppercase tracking-[0.3em] text-[#C5A059]">
              {t.contact.eyebrow}
            </span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-white leading-[1.05]">
            {t.contact.title}
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
          {/* Info column */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5 flex flex-col gap-6"
          >
            {/* Phone card */}
            <a
              href={`tel:${SALON_INFO.phoneIntl}`}
              className="group block bg-[#0c0c0c] border border-white/5 hover:border-[#C5A059]/50 p-8 transition-all duration-500"
              data-testid="contact-phone-card"
            >
              <div className="flex items-start justify-between mb-4">
                <Phone size={24} className="text-[#C5A059]" strokeWidth={1.5} />
                <ArrowUpRight
                  size={18}
                  className="text-white/30 group-hover:text-[#C5A059] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all"
                  strokeWidth={1.5}
                />
              </div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">
                {t.contact.phone_label}
              </div>
              <div className="font-display text-2xl md:text-3xl text-white group-hover:text-[#C5A059] transition-colors">
                {SALON_INFO.phone}
              </div>
              <div className="mt-3 text-xs uppercase tracking-[0.2em] text-[#C5A059]">
                {t.contact.call_cta} →
              </div>
            </a>

            {/* Reservation card */}
            <a
              href={SALON_INFO.notino}
              target="_blank"
              rel="noopener noreferrer"
              className="group block bg-gradient-to-br from-[#C5A059]/15 to-[#0c0c0c] border border-[#C5A059]/30 hover:border-[#C5A059] p-8 transition-all duration-500"
              data-testid="contact-reserve-card"
            >
              <div className="flex items-start justify-between mb-4">
                <Calendar size={24} className="text-[#C5A059]" strokeWidth={1.5} />
                <ArrowUpRight
                  size={18}
                  className="text-[#C5A059] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all"
                  strokeWidth={1.5}
                />
              </div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">
                {t.contact.reserve_label}
              </div>
              <div className="font-display text-2xl md:text-3xl text-white">
                {t.contact.reserve_text}
              </div>
              <div className="mt-3 text-xs uppercase tracking-[0.2em] text-[#C5A059]">
                {t.contact.reserve_cta} →
              </div>
            </a>

            {/* Address */}
            <div
              className="bg-[#0c0c0c] border border-white/5 p-8"
              data-testid="contact-address-card"
            >
              <div className="flex items-start gap-4">
                <MapPin size={22} className="text-[#C5A059] flex-shrink-0 mt-1" strokeWidth={1.5} />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">
                    {t.contact.address_label}
                  </div>
                  <div className="font-display text-xl md:text-2xl text-white leading-snug">
                    {SALON_INFO.address}
                  </div>
                  <a
                    href={`https://www.google.com/maps?q=${encodeURIComponent(SALON_INFO.address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 inline-block text-xs uppercase tracking-[0.2em] text-[#C5A059] hover:text-[#E8D3A6]"
                    data-testid="contact-directions-link"
                  >
                    Otvoriť v mape →
                  </a>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Map */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-7"
            data-testid="contact-map-wrapper"
          >
            <div className="relative h-full min-h-[420px] lg:min-h-full overflow-hidden border border-white/5 bg-[#0c0c0c]">
              <iframe
                title="Mapa - Luxury Salón Žilina"
                src={SALON_INFO.mapEmbed}
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: 420 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="map-dark"
                data-testid="contact-map"
              />
              <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-[#C5A059]/10" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
